import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

function ChangeOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await api.getChangeOrders();
      setOrders(data);
    } catch (err) {
      setError(err.message || 'Failed to load change orders');
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = '$') => {
    if (!amount) return `${currency}0.00`;
    return `${currency}${parseFloat(amount).toFixed(2)}`;
  };

  // Filter orders based on status and search
  const filteredOrders = orders.filter((order) => {
    const matchesFilter = filter === 'all' || order.status === filter;
    const matchesSearch = !searchQuery ||
      order.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.job_title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'bg-slate-100 text-slate-700',
      sent: 'bg-blue-100 text-blue-800',
      signed: 'bg-green-100 text-green-800',
    };
    return statusStyles[status] || 'bg-slate-100 text-slate-700';
  };

  // Get counts for filter badges
  const getCounts = () => {
    return {
      all: orders.length,
      draft: orders.filter(o => o.status === 'draft').length,
      sent: orders.filter(o => o.status === 'sent').length,
      signed: orders.filter(o => o.status === 'signed').length,
    };
  };

  const counts = getCounts();

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading change orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-primary mb-2">Change Orders</h1>
            <p className="text-slate-600">Track scope changes and get signatures</p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              placeholder="Search change orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'draft', label: 'Draft' },
            { key: 'sent', label: 'Sent' },
            { key: 'signed', label: 'Signed' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                filter === key
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-primary'
              }`}
            >
              {label}
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                filter === key ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {counts[key]}
              </span>
            </button>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            {error}
          </div>
        )}

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200 text-center">
            <i className="fas fa-file-signature text-6xl text-slate-300 mb-4"></i>
            <h3 className="text-xl font-bold text-slate-700 mb-2">No Change Orders Found</h3>
            <p className="text-slate-600 mb-6">
              {orders.length === 0
                ? 'Create change orders in the mobile app to track scope changes.'
                : 'No change orders match your current filters.'}
            </p>
            {orders.length > 0 && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilter('all');
                }}
                className="text-secondary font-medium hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredOrders.map((order) => (
              <Link
                key={order.id}
                to={`/change-orders/${order.id}`}
                className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:border-secondary/30 transition block"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-primary">
                        {order.title || 'Change Order'}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                    {order.client_name && (
                      <p className="text-slate-600 mb-1">
                        <i className="fas fa-user mr-2 text-slate-400"></i>
                        {order.client_name}
                      </p>
                    )}
                    {order.job_title && (
                      <p className="text-sm text-slate-500">
                        <i className="fas fa-briefcase mr-2 text-slate-400"></i>
                        {order.job_title}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Total</p>
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(order.total)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Created</p>
                      <p className="text-sm font-medium text-slate-700">
                        {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {order.status === 'signed' ? (
                      <div className="flex items-center text-green-600">
                        <i className="fas fa-check-circle text-xl"></i>
                      </div>
                    ) : (
                      <i className="fas fa-chevron-right text-slate-400"></i>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Summary */}
        {filteredOrders.length > 0 && (
          <div className="mt-6 text-center text-slate-600">
            Showing {filteredOrders.length} of {orders.length} change orders
          </div>
        )}
      </div>
    </div>
  );
}

export default ChangeOrders;
